import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-alpine.css";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { getOtherProducts } from "../../../../services/apiService";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";

const OtherProduct = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [rowData, setRowData] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter and Sort states
  const [filterColumn, setFilterColumn] = useState("oth_Product_Name");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [totalCount, setTotalCount] = useState();
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset pagination when switching modes
  useEffect(() => {
    setCurrentPage(1);
    setItemsPerPage(10); // or your default
  }, [isMobile]);

  useEffect(() => {
    const options = [
      { value: "oth_Product_Name", label: "Product Name" },
      { value: "oth_Product_ShortName", label: "Short Name" },
      { value: "oth_Product_insurerId", label: "Insurer" },
      { value: "is_Members", label: "Is Members" },
      { value: "is_Renewable", label: "Is Renewable" },
    ];
    setColumnOptions(options);
    fetchOtherProductData();
  }, []);

  const fetchOtherProductData = async () => {
    try {
      setLoading(true);
      const requestPayload = {
        TObject: {
          CreatedBy: 1,
          filterColumn: isFilterApplied ? filterColumn : null,
          filterValue: isFilterApplied ? filterValue.trim() : null,
          sortField: sortField,
          sortOrder: sortDirection,
        },
      };
      const response = await getOtherProducts(requestPayload);
      const data = response?.data?.tObjectLst || [];
      setRowData(data);
      setTotalCount(data.length)
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch other product data");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    if (!filterValue.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setIsFilterApplied(true);
    setCurrentPage(1);
    await fetchOtherProductData();
  };

  const clearFilters = async () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    await fetchOtherProductData();
  };

  const handleSort = async (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
    await fetchOtherProductData();
  };
  const createHeaderComponent = (field, displayName) => {
    return function CustomHeader() {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };
  // Update header components when sort state changes
  useEffect(() => {
    if (columnDefs.length > 0) {
      const updatedColumnDefs = columnDefs.map(col => {
        if (col.headerComponent) {
          return {
            ...col,
            headerComponent: createHeaderComponent(col.field, col.headerName)
          };
        }
        return col;
      });
      setColumnDefs(updatedColumnDefs);
    }
  }, [sortField, sortDirection]);


  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Product Name",
        field: "oth_Product_Name",
        flex: 1,
        headerComponent: createHeaderComponent("oth_Product_Name", "Product Name"),
        suppressMenu: true,
      },
      {
        headerName: "Short Name",
        field: "oth_Product_ShortName",
        flex: 1,
        headerComponent: createHeaderComponent("oth_Product_ShortName", "Short Name"),
        suppressMenu: true,
      },
      {
        headerName: "Insurer",
        field: "oth_Product_insurerId",
        flex: 1,
        headerComponent: createHeaderComponent("oth_Product_insurerId", "Insurer"),
        suppressMenu: true,
      },
      {
        headerName: "Is Members",
        field: "is_Members",
        flex: 1,
        headerComponent: createHeaderComponent("is_Members", "Is Members"),
        suppressMenu: true,
        cellRenderer: (params) => {
          const value = params.value === true || params.value === "true" || params.value === 1;
          return value ? (
            <span className="badge bg-success">Yes</span>
          ) : (
            <span className="badge bg-brandRed">No</span>
          );
        },
        suppressMenu: true,
      },
      {
        headerName: "Is Renewable",
        field: "is_Renewable",
        flex: 1,
        headerComponent: createHeaderComponent("is_Renewable", "Is Renewable"),
        suppressMenu: true,
        cellRenderer: (params) => {
          const value = params.value === true || params.value === "true" || params.value === 1;
          return value ? (
            <span className="badge bg-success">Yes</span>
          ) : (
            <span className="badge bg-brandRed">No</span>
          );
        },
      },

      {
        headerName: "Action",
        field: "action",
        headerClass: 'action-header',
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1" >
            <button
              className="edit-button"
              onClick={() => navigate(`/other-product/edit/${params.data.oth_Product_ID}`)}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen size={18} color="#787474" className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(params.data.oth_Product_ID);
                setDeleteMode("single");
                onOpen();
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
        // headerClass: 'ag-left-aligned-header',
        sortable: false,
      },
    ];

    setColumnDefs(createColumnDefs());
  }, []);

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

      selectedIdsRef.current = selectedNodes.map((node) => node.data.oth_Product_ID);
      setSelectedNames(selectedNodes.map((node) => node.data.oth_Product_Name));

      if (selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0) {
        setAllSelected(true);
        setDeleteMode("multi");
        onOpen();
      } else {
        setAllSelected(false);
      }
    } catch (error) {
      console.error("Error in onSelectionChanged:", error);
    }
  };

  const onOpen = () => {
    if (!gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      selectedIdsRef.current = selectedNodes.map((node) => node.data.oth_Product_ID);
      setSelectedNames(selectedNodes.map((node) => node.data.oth_Product_Name));
      setPreserveSelection(true);
      setIsOpen(true);
    } catch (error) {
      console.error("Error in onOpen:", error);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const confirmDelete = () => {
    setRowData((prev) => {
      if (deleteMode === "multi" || allSelected) {
        return prev.filter((row) => !selectedIdsRef.current.includes(row.oth_Product_ID));
      } else {
        return prev.filter((row) => row.oth_Product_ID !== deleteId);
      }
    });
    toast.success("Branch deleted successfully");
    onClose();
  };

  const paginatedData = rowData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  useEffect(() => {
    const totalPages = Math.ceil(rowData.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [rowData, currentPage, itemsPerPage]);

  useEffect(() => {
    if (preserveSelection && selectedIdsRef.current.length > 0 && gridRef.current?.api) {
      try {
        gridRef.current.api.forEachNode((node) => {
          if (selectedIdsRef.current.includes(node.data.oth_Product_ID)) {
            node.setSelected(true);
          }
        });
      } catch (error) {
        console.error("Error in preserveSelection effect:", error);
      }
    }
  }, [rowData, currentPage, preserveSelection]);

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Other Product Master List</h2>
        <button
          className="px-4 py-2 addListTable-button"
          onClick={() => navigate("/other-product/add")}
        >
          Add
        </button>
      </div>


      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={isMobile ? rowData : paginatedData}
        paginatedData={isMobile ? rowData : paginatedData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={loading}
        handleItemsPerPageChange={handleItemsPerPageChange}
        isMobile={isMobile}
        totalCount={totalCount}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${paginatedData.length} other products on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected other products?`
              : "Are you sure you want to delete this other product?"
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default OtherProduct;
